import { Inject, Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { delay, interval, map, Observable, of, Subject, Subscriber } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthSourceApple, AuthSourceGoogle, AuthSourceName, AuthSourceVK, RefreshTokenName, VerifierName } from '../const/AuthConst';
import { OAuthService } from 'angular-oauth2-oidc';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { LogService } from './log.service';
import { HistoryEventService } from './history.event.service';

declare function getOsName(): any;
declare function getOsVersion(): any;
declare function getTimeZone(): any;

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private httpClient = inject(HttpClient);
  private oAuthService = inject(OAuthService);
  private accessToken: string;
  private accountId: string;
  private helper = new JwtHelperService();
  private acccountInfo: AccountInfo = {} as AccountInfo;
  private isProcess: boolean = false;

  constructor(
    @Inject('BASE_URL') private baseUrl: string,
    private router: Router,
    private logService: LogService,
    private historyEventService: HistoryEventService
  ) {
    this.accessToken = "";
    this.accountId = "";
  }

  /**
   * Получение токена DexBee на основе токена Google
   * POST /v3/oauth/login/google/web
   * @returns Строка с токеном
   */
  getAccountTokenGoogle(idToken: string): Observable<AccountLoginResponse> {
    var request: AccountLogin = {
      clientToken: idToken,
      code: ""
    }

    var url = this.baseUrl + '/v3/oauth/login/google/web';
    if (environment.environment == 'local') {
      url = environment.apiUrl5001 + '/v3/oauth/login/google/web';
    };

    var res = this.httpClient.post<AccountLoginResponse>(url,
      request
    );

    return res;
  }

  /**
 * Получение токена DexBee на основе токена Apple
 * POST /v3/oauth/login/apple/web
 * @returns Строка с токеном
 */
  getAccountTokenApple(code: string, idToken: string): Observable<AccountLoginResponse> {

    var request: AccountLogin = {
      clientToken: idToken,
      code: code
    }

    var url = this.baseUrl + '/v3/oauth/login/apple/web';
    if (environment.environment == 'local') {
      url = environment.apiUrl5001 + '/v3/oauth/login/apple/web';
    };

    var res = this.httpClient.post<AccountLoginResponse>(url,
      request
    );

    return res;
  }


  /**
 * Получение токена DexBee на основе токена VK
 * POST /v3/oauth/login/vk/web
 * @returns Строка с токеном
 */
  getAccountTokenVK(code: string, deviceId: string): Observable<AccountLoginResponse> {

    var request: AccountVkLogin = {
      device_id: deviceId,
      code: code,
      verifier: this.getVerifier()
    }

    var url = this.baseUrl + '/v3/oauth/login/vk/web';
    if (environment.environment == 'local') {
      url = environment.apiUrl5001 + '/v3/oauth/login/vk/web';
    };

    var res = this.httpClient.post<AccountLoginResponse>(url,
      request
    );

    return res;
  }

  /**
 * Обновление токена
 * @returns Строка с токеном
 */
  refreshToken(methodName: string): Observable<AuthRefreshResponse> {
    var request: AuthRefresh = {
      refreshToken: this.getRefreshToken(),
      methodName: methodName
    }

    var url = this.baseUrl + '/v3/auth/refresh/web';
    if (environment.environment == 'local') {
      url = environment.apiUrl5001 + '/v3/auth/refresh/web';
    };

    var res = this.httpClient.post<AuthRefreshResponse>(url,
      request
    );

    return res;
  }

  /**
 * Отправка кода подтверждения на почту
 * POST /v3/oauth/login/email/send/web
 * @returns Строка с токеном
 */
  accountSendCode(email: string): Observable<AccountLoginResponse> {

    var request: AccountEmailSend = {
      email: email
    }

    const methodUrl = '/v3/oauth/login/email/send/web';
    var url = this.baseUrl + methodUrl;
    if (environment.environment == 'local') {
      url = environment.apiUrl5001 + methodUrl;
    };

    var res = this.httpClient.post<AccountLoginResponse>(url,
      request
    );

    return res;
  }

  /**
 * Отправка проверки кода подтверждения
 * POST /v3/oauth/login/email/check/web
 * @returns Строка с токеном
 */
  accountCheckCode(email: string, code: string): Observable<AccountLoginResponse> {

    var request: AccountEmailCheck = {
      email: email,
      code: code
    }

    const methodUrl = '/v3/oauth/login/email/check/web';
    var url = this.baseUrl + methodUrl;
    if (environment.environment == 'local') {
      url = environment.apiUrl5001 + methodUrl;
    };

    var res = this.httpClient.post<AccountLoginResponse>(url,
      request
    );

    return res;
  }

  /**
 * Отправка текста ошибки аутентификации
 * POST /v3/oauth/error
 * @returns Строка с токеном
 */
  authSendError(userId: string, errorMessage: string, eventType: string) {

    var request: AuthError = {
      userId: userId,
      errorMessage: errorMessage,
      eventType: eventType
    }

    const methodUrl = '/v3/oauth/error';
    var url = this.baseUrl + methodUrl;
    if (environment.environment == 'local') {
      url = environment.apiUrl5001 + methodUrl;
    };

    this.httpClient.post<number>(url, request).subscribe();
  }

  public setAccessToken(token: string) {
    localStorage.setItem("token", token);
    this.accessToken = token;
  }

  /**
   * Извлечение токена из локального хранилища
   * @returns Строка с токеном
   */
  public getAccessToken() {
    this.accessToken = localStorage.getItem("token") || "";
    if (this.accessToken == "undefined") {
      this.accessToken = "";
    }
    return this.accessToken;
  }

  public setAccountId(accountId: string) {
    localStorage.setItem("accountId", accountId);
    this.accountId = accountId;
  }

  public getAccountId() {
    this.accountId = localStorage.getItem("accountId") || "";
    return this.accountId;
  }

  public setAuthSource(authSource: string) {
    localStorage.setItem(AuthSourceName, authSource);
  }

  public isAuthSourceGoogle() {
    return localStorage.getItem(AuthSourceName) == AuthSourceGoogle;
  }

  public isAuthSourceVK() {
    return localStorage.getItem(AuthSourceName) == AuthSourceVK;
  }

  public setRefreshToken(token: string) {
    localStorage.setItem(RefreshTokenName, token);
  }

  public getRefreshToken() {
    return localStorage.getItem(RefreshTokenName) || "";
  }

  public setVerifier(verifier: string) {
    localStorage.setItem(VerifierName, verifier);
  }

  public getVerifier() {
    return localStorage.getItem(VerifierName) || "";
  }

  private checkRefresh(): boolean {
    var isExpired = false;
    var token = this.getAccessToken();
    if (token.length != 0) {
      isExpired = this.helper.isTokenExpired(token);
    }

    return isExpired;
  }

  private processRefreshToken(subscriber: Subscriber<any>, methodName: string) {
    this.isProcess = true;
    this.refreshToken(methodName)
      .subscribe({
        next: (data: AuthRefreshResponse) => {
          this.setAccessToken(data.token)
          this.setRefreshToken(data.refreshToken);
          this.isProcess = false;
          subscriber.next(data);
          subscriber.complete();
        },
        error: (e) => {
          this.isProcess = false;
          this.sessionEnd();
        }
      });
  }

  /**
   * Проверяет токен доступа на срок действия, в случае устаревания - обновляет
   */
  public checkExpireToken(methodName: string): Observable<any> {
    return new Observable(subscriber => {
      var isExpired = this.checkRefresh();
      if (isExpired) {
        this.logService.LogMessage(methodName + ". Access token is expired")
        if (this.isProcess) {
          setTimeout(() => {
            this.processRefreshToken(subscriber, methodName);
          }, 200);
        }
        else {
          this.processRefreshToken(subscriber, methodName);
        }
      }
      else {
        subscriber.next([]);
        subscriber.complete();
      }
    })
  }


  public sessionEnd() {
    localStorage.removeItem("token");
    localStorage.removeItem("accountId");
    localStorage.removeItem(AuthSourceName);
    localStorage.removeItem(RefreshTokenName);
    this.router.navigate([""]);
  }

  public logOut() {
    if (this.isAuthSourceGoogle()) {
      this.oAuthService.revokeTokenAndLogout();
      this.oAuthService.logOut();
    }
    localStorage.removeItem("token");
    localStorage.removeItem("accountId");
    localStorage.removeItem(AuthSourceName);
    localStorage.removeItem(RefreshTokenName);
  }

  public getApiAccountInfo(): Observable<AccountInfo> {
    return this.httpClient.get<AccountInfo>(this.baseUrl + '/v1/subscription/account/info', {
      headers: {
        'X-Account-Id': this.getAccountId(),
        'Authorization': 'Bearer ' + this.getAccessToken(),
      },
    });
  }

  public getApiAccountProfile(): Observable<AccountProfileInfo> {
    const methodUrl = '/v3/accounts/info';
    var url = this.baseUrl + methodUrl;
    if (environment.environment == 'local') {
      url = environment.apiUrl5001 + methodUrl;
    };

    return this.httpClient.get<AccountProfileInfo>(url, {
      headers: {
        'X-Account-Id': this.getAccountId(),
        'Authorization': 'Bearer ' + this.getAccessToken(),
      },
    });
  }


  public getAppConfig(): Observable<AppConfigResponse> {
    var url = this.baseUrl + '/v1/settings/AppConfig';
    if (environment.environment == 'local') {
      url = environment.apiUrl5001 + '/v1/settings/AppConfig';
    };

    return this.httpClient.get<AppConfigResponse>(url, {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Device-Screen-Width': window.screen.width.toString(),
        'X-Device-Screen-Height': window.screen.width.toString(),
        'X-Device-OS-Name': getOsName(),
        'X-Device-OS-Version': getOsVersion(),
        'X-Device-Timezone': getTimeZone(),
        'X-Device-Pixel-Ratio': (window.devicePixelRatio || 1).toString(),
      },
    });
  }

  public getAccountInfo() {
    return this.acccountInfo;
  }

  public setAccountInfo(data: AccountInfo) {
    this.acccountInfo = data;
  }


  /**
   * Проверка аккаунта на наличие профиля и запись переменных в хранилище
   */
  public checkProfileAndSetInfo(data: AccountLoginResponse, authSource: string) {
    this.setAccessToken(data.token)
    this.setAccountId(data.accountId);
    this.setRefreshToken(data.refreshToken);
    this.setAuthSource(authSource);

    this.getApiAccountProfile()
      .subscribe({
        next: (info) => {
          if (info.profileId == null) {
            this.logOut();
            this.router.navigate(['noaccount']);
          }
          else {
            this.historyEventService.setEvent('account');
            if (authSource != AuthSourceGoogle) {
              this.router.navigate(["/main"]);
            }
          }
        }
      });
  }

}

